import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { CheckCircle, ArrowLeft, Phone, Car, UserCheck, Sparkles, Shield, ChevronLeft, ChevronRight, X, ZoomIn } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GUARANTEES = [
  { icon: Car, title: "Véhicule de luxe", desc: "Entretien impeccable" },
  { icon: UserCheck, title: "Chauffeur inclus", desc: "Professionnel en tenue" },
  { icon: Sparkles, title: "Décoration", desc: "Personnalisée à votre thème" },
  { icon: Shield, title: "Champagne offert", desc: "Pour célébrer votre union" },
];

// Lightbox component for fullscreen image viewing
function Lightbox({ images, currentIndex, onClose, onPrev, onNext }) {
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose, onPrev, onNext]);

  const getImageUrl = (img) => {
    if (img.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${img}`;
    return img;
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" onClick={onClose}>
      <button onClick={onClose} className="absolute top-4 right-4 text-white/80 hover:text-white p-2 z-10">
        <X size={28} />
      </button>
      
      {images.length > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); onPrev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-3 bg-black/30 rounded-full hover:bg-black/50 transition-colors">
            <ChevronLeft size={32} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onNext(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-3 bg-black/30 rounded-full hover:bg-black/50 transition-colors">
            <ChevronRight size={32} />
          </button>
        </>
      )}
      
      <img 
        src={getImageUrl(images[currentIndex])} 
        alt="" 
        className="max-w-[90vw] max-h-[85vh] object-contain"
        onClick={(e) => e.stopPropagation()}
      />
      
      {/* Thumbnail strip */}
      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 bg-black/50 p-2 rounded-lg">
          {images.map((img, i) => (
            <button key={i} onClick={(e) => { e.stopPropagation(); /* handled by parent */ }}
              className={`w-12 h-12 rounded overflow-hidden border-2 transition-all ${i === currentIndex ? "border-[#D4A843] scale-110" : "border-transparent opacity-60 hover:opacity-100"}`}>
              <img src={getImageUrl(img)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
      
      {/* Counter */}
      <div className="absolute top-4 left-4 text-white/80 text-sm font-medium bg-black/30 px-3 py-1 rounded-full">
        {currentIndex + 1} / {images.length}
      </div>
    </div>
  );
}

export default function VehicleDetailPage() {
  const { id } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [selectedImg, setSelectedImg] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/vehicles/${id}`)
      .then(r => { setVehicle(r.data); setSelectedImg(0); })
      .finally(() => setLoading(false));
  }, [id]);

  const getImageUrl = (img) => {
    if (img?.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${img}`;
    return img;
  };

  const nextImage = () => {
    if (vehicle?.images?.length > 1) {
      setSelectedImg((prev) => (prev + 1) % vehicle.images.length);
    }
  };

  const prevImage = () => {
    if (vehicle?.images?.length > 1) {
      setSelectedImg((prev) => (prev - 1 + vehicle.images.length) % vehicle.images.length);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#D4A843] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!vehicle) return (
    <div className="min-h-screen flex items-center justify-center">
      <p className="text-gray-500">Véhicule introuvable</p>
    </div>
  );

  const images = vehicle.images || [];

  return (
    <div className="font-sans">
      <Navbar />
      <div className="pt-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
          <Link to="/vehicules" data-testid="back-to-vehicles"
            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 mb-8 transition-colors">
            <ArrowLeft size={16} /> Retour aux véhicules
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Images Gallery */}
            <div>
              {/* Main Image */}
              <div className="relative overflow-hidden h-80 mb-3 group cursor-pointer bg-gray-100"
                   onClick={() => images.length > 0 && setLightboxOpen(true)}>
                {images.length > 0 ? (
                  <>
                    <img src={getImageUrl(images[selectedImg])} alt={vehicle.name}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" 
                      data-testid="vehicle-main-image" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                      <ZoomIn size={32} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    {/* Navigation arrows on main image */}
                    {images.length > 1 && (
                      <>
                        <button onClick={(e) => { e.stopPropagation(); prevImage(); }}
                          className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/80 hover:bg-white rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity">
                          <ChevronLeft size={20} className="text-gray-800" />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); nextImage(); }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/80 hover:bg-white rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity">
                          <ChevronRight size={20} className="text-gray-800" />
                        </button>
                      </>
                    )}
                    {/* Image counter */}
                    {images.length > 1 && (
                      <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                        {selectedImg + 1} / {images.length}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <Car size={48} />
                  </div>
                )}
              </div>
              
              {/* Thumbnails */}
              {images.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {images.map((img, i) => (
                    <button key={i} onClick={() => setSelectedImg(i)}
                      data-testid={`vehicle-thumb-${i}`}
                      className={`flex-shrink-0 overflow-hidden h-20 w-24 border-2 transition-all rounded ${
                        selectedImg === i ? "border-[#D4A843] shadow-lg" : "border-transparent opacity-60 hover:opacity-100"
                      }`}>
                      <img src={getImageUrl(img)} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Info */}
            <div>
              <p className="text-xs font-semibold tracking-[0.3em] text-[#D4A843] uppercase mb-2">{vehicle.brand}</p>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">{vehicle.name}</h1>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-3xl font-bold text-[#D4A843]">{vehicle.price}€</span>
                <span className="text-gray-400 text-sm">/ prestation</span>
              </div>
              <p className="text-gray-500 leading-relaxed mb-6">{vehicle.description}</p>

              <div className="mb-8">
                <p className="text-xs font-semibold tracking-widest text-gray-700 uppercase mb-4">Inclus dans la prestation</p>
                <ul className="space-y-2">
                  {vehicle.features?.map(f => (
                    <li key={f} className="flex items-center gap-2 text-gray-700">
                      <CheckCircle size={16} className="text-[#D4A843]" /> {f}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Link to={`/reservation/${vehicle.id}`} data-testid="reserver-vehicle-btn"
                  className="flex-1 text-center py-4 bg-[#D4A843] text-white font-semibold tracking-wider hover:bg-[#c49933] transition-all duration-200">
                  Réserver ce véhicule
                </Link>
                <a href="tel:0692042073" data-testid="call-btn"
                  className="flex-1 text-center py-4 border-2 border-gray-900 text-gray-900 font-semibold tracking-wider hover:bg-gray-900 hover:text-white transition-all duration-200 flex items-center justify-center gap-2">
                  <Phone size={16} /> Nous appeler
                </a>
              </div>
            </div>
          </div>

          {/* Guarantees */}
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Nos garanties</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {GUARANTEES.map(({ icon: Icon, title, desc }) => (
                <div key={title} className="text-center p-6 bg-[#f8f5ef]">
                  <Icon size={28} className="text-[#D4A843] mx-auto mb-3" />
                  <p className="font-bold text-gray-900 text-sm mb-1">{title}</p>
                  <p className="text-xs text-gray-500">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Footer />

      {/* Lightbox */}
      {lightboxOpen && images.length > 0 && (
        <Lightbox 
          images={images}
          currentIndex={selectedImg}
          onClose={() => setLightboxOpen(false)}
          onPrev={prevImage}
          onNext={nextImage}
        />
      )}
    </div>
  );
}
